/**
 * Session ACL klien — menu filter & wewenang.
 */
import { pathAllowed, normalizeAppPath } from '@/utils/menuAcl';

export type AuthMe = {
  ok: boolean;
  authenticated: boolean;
  user?: string;
  userId?: number;
  displayName?: string;
  email?: string | null;
  levelCode?: string;
  levelName?: string;
  levelColor?: string;
  rank?: number;
  canManageUsers?: boolean;
  isSystem?: boolean;
  mustChangePassword?: boolean;
  employeeId?: number | null;
  taxClientId?: number | null;
  menus?: string[] | '*';
  exp?: number;
};

const defaultMe = (): AuthMe => ({ ok: false, authenticated: false });

export function useAuthSession() {
  const me = useState<AuthMe>('jgi-auth-me', defaultMe);
  const loaded = useState<boolean>('jgi-auth-me-loaded', () => false);
  const loading = useState<boolean>('jgi-auth-me-loading', () => false);

  async function refresh() {
    if (!import.meta.client) return me.value;
    loading.value = true;
    try {
      const data = await $fetch<AuthMe>('/api/auth/me');
      me.value = data || defaultMe();
      if (data?.authenticated && data.user) {
        try {
          const prev = JSON.parse(localStorage.getItem('jgi_auth') || '{}');
          localStorage.setItem(
            'jgi_auth',
            JSON.stringify({
              ...prev,
              user: data.user,
              displayName: data.displayName,
              levelCode: data.levelCode,
              levelName: data.levelName,
              at: Date.now(),
            })
          );
        } catch {
          /* ignore */
        }
      }
      return me.value;
    } catch {
      me.value = defaultMe();
      return me.value;
    } finally {
      loaded.value = true;
      loading.value = false;
    }
  }

  function canAccess(path: string) {
    if (!me.value.authenticated) return false;
    const menus = me.value.menus;
    if (!menus) return true;
    return pathAllowed(path, menus);
  }

  function filterMenuTree<T extends { to?: string; children?: T[]; chip?: string }>(items: T[]): T[] {
    return items
      .filter((item) => item.chip !== 'Pro')
      .map((item) => {
        const children = item.children ? filterMenuTree(item.children) : undefined;
        return { ...item, children };
      })
      .filter((item) => {
        if (item.children) return item.children.length > 0;
        if (!item.to) return true;
        return canAccess(item.to);
      });
  }

  return {
    me,
    loaded,
    loading,
    refresh,
    canAccess,
    filterMenuTree,
    normalizeAppPath,
  };
}
